import React, { useMemo, useCallback } from 'react';
import { BaseTable, BaseTableProps } from './BaseTable';
import { useExcelNavigation } from '@/hooks/useExcelNavigation';
import { useExcelShortcuts } from '@/hooks/useExcelShortcuts';

export interface ExcelTableWrapperProps<T> extends BaseTableProps<T> {
  // Excel-specific props
  enableExcelNavigation?: boolean;
  enableKeyboardShortcuts?: boolean;
  onCellSelect?: (rowIndex: number, colIndex: number, cell: HTMLElement) => void;
  onCellEdit?: (rowIndex: number, colIndex: number, value: any, item: T) => void;
  onCellDoubleClick?: (rowIndex: number, colIndex: number, item: T) => void;
  
  // Custom renderer for editable cells
  renderEditableCell?: (
    item: T, 
    column: any, 
    index: number,
    isEditing: boolean,
    onStartEdit: () => void,
    onEndEdit: (value: any) => void
  ) => React.ReactNode;
}

export function ExcelTableWrapper<T extends Record<string, any>>({
  data,
  columns,
  enableExcelNavigation = true,
  enableKeyboardShortcuts = true,
  onCellSelect,
  onCellEdit,
  onCellDoubleClick,
  renderEditableCell,
  ...baseTableProps
}: ExcelTableWrapperProps<T>) {
  
  // Excel navigation hook
  const {
    tableRef,
    currentCell,
    focusCell,
    navigateCell,
    startEditing,
    stopEditing
  } = useExcelNavigation({
    data,
    columns,
    onCellSelect,
    onCellEdit: (rowIndex, colIndex, value) => {
      const item = data[rowIndex];
      onCellEdit?.(rowIndex, colIndex, value, item);
    },
    enableEditing: true
  });

  // Excel shortcuts hook
  useExcelShortcuts({
    onSelectAll: () => {
      baseTableProps.onToggleAllSelection?.(true);
    },
    onClearSelection: () => {
      baseTableProps.onToggleAllSelection?.(false);
    },
    onCopy: () => {
      // Implement copy logic based on your store
      console.log('Copy triggered');
    },
    onPaste: () => {
      // Implement paste logic
      console.log('Paste triggered');
    },
    onDelete: () => {
      // Implement delete logic
      console.log('Delete triggered');
    },
    onNavigate: navigateCell,
    onNewRow: () => {
      // Implement new row logic
      console.log('New row triggered');
    },
    onSave: () => {
      console.log('Save triggered');
    }
  });

  // Enhanced columns with Excel functionality
  const excelColumns = useMemo(() => {
    return columns.map((column, colIndex) => ({
      ...column,
      render: (item: T, rowIndex: number) => {
        const isCurrentCell = currentCell.row === rowIndex && currentCell.col === colIndex;    
        const isPriorityOrStatus = column.key === 'priority' || column.key === 'status';

        // Use custom renderer if provided
        if (renderEditableCell) {
          return renderEditableCell(
            item,
            column,
            rowIndex,
            isCurrentCell,
            () => startEditing(),
            (value) => onCellEdit?.(rowIndex, colIndex, value, item)
          );
        }

        // Default Excel-like cell rendering
        return (
          <div
            data-excel-cell="true"
            data-editable="true"
            data-row={rowIndex}
            data-col={colIndex}
            tabIndex={0}
            className={`
              w-full h-full min-h-[2rem] flex  items-center 
              outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
              ${isCurrentCell ? 'bg-blue-50 ring-1 ring-blue-300' : ''}
              hover:bg-gray-50  ${isPriorityOrStatus ? 'justify-center' : ''}
            `}
            onDoubleClick={() => {
              startEditing();
              onCellDoubleClick?.(rowIndex, colIndex, item);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                startEditing();
              }
            }}
          >
            {column.render ? column.render(item, rowIndex) : item[column.key]}
          </div>
        );
      }
    }));
  }, [columns, currentCell, renderEditableCell, startEditing, onCellEdit, onCellDoubleClick]);

  // Enhanced row click handler
  const handleRowClick = useCallback((item: T) => {
    baseTableProps.onRowClick?.(item);
  }, [baseTableProps]);

  // Enhanced row class name
  const getEnhancedRowClassName = useCallback((item: T, index: number) => {
    const baseClassName = baseTableProps.getRowClassName?.(item, index) || '';
    const isSelected = baseTableProps.selectedItems?.includes(
      baseTableProps.getItemId?.(item) || ''
    );
    
    return `
      ${baseClassName}
      ${isSelected ? 'bg-blue-50' : ''}
      ${index === currentCell.row ? 'ring-1 ring-blue-200' : ''}
      hover:bg-gray-50
    `;
  }, [baseTableProps, currentCell.row]);

  return (
    <div className="excel-table-wrapper w-full h-full overflow-auto">
      <div className='min-w-full max-w-full h-full'>
      <BaseTable
        ref={tableRef}
        data={data}
        columns={excelColumns}
        {...baseTableProps}
        onRowClick={handleRowClick}
        getRowClassName={getEnhancedRowClassName}
        tableClassName={`
          ${baseTableProps.tableClassName || ''}
          excel-like-table
          border-collapse
          [&_td]:border [&_td]:border-gray-300
          [&_th]:border [&_th]:border-gray-400
          [&_th]:bg-gray-100 [&_th]:font-semibold
        `}
        containerClassName={`
          ${baseTableProps.containerClassName || ''}
          excel-table-container
        `}
      />
      </div>
    </div>
  );
}